import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../Layout';
import axiosInstance from '../../utils/axiosInstance';
import {
  Title,
  Text,
  Icon,
  MessageBox,
  MessageBoxType,
  TabContainer,
  Tab,
} from '@ui5/webcomponents-react';

import RegionalConfig from './tabs/RegionalConfig';
import ApplicationConfig from './tabs/ApplicationConfig';
import CompanyConfig from './tabs/CompanyConfig';
import SecurityConfig from './tabs/SecurityConfig';
import NotificationsConfig from './tabs/NotificationsConfig';
import OrdersConfig from './tabs/OrdersConfig';
import DocumentsConfig from './tabs/DocumentsConfig';
import IntegrationsConfig from './tabs/IntegrationsConfig';
import FarosBiConfig from './tabs/FarosBiConfig';
import useAutoTranslate from '../../hooks/useAutoTranslate';

function SystemConfig() {
  console.log('SystemConfig: Component rendering');

  const [activeTab, setActiveTab] = useState("regional");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const [allFormData, setAllFormData] = useState({
    regional: null,
    application: null,
    company: null,
    security: null,
    notifications: null,
    orders: null,
    documents: null,
    integrations: null,
    farosbi: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const { autoT } = useAutoTranslate();

  // Cargar configuraciones una sola vez al montar el componente
  useEffect(() => {
    const loadSettings = async () => {
      console.log('SystemConfig: Loading settings');
      setIsLoading(true);
      setError(null);
      
      try {
        const userDataString = localStorage.getItem("userData");
        let tenantId;
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          tenantId = userData.tenantId;
          console.log("tenantId:", tenantId);
        } else {
          throw new Error("No se encontró userData en localStorage.");
        }

        const response = await axiosInstance.get(`tenant-configurations/${tenantId}`);
        
        if (response.data.code === 'SUCCESS') {
          const settings = response.data.data.systemSettings;
          console.log('SystemConfig: API settings loaded:', settings);
          setSystemSettings(settings);
          
          // Inicializar todos los formData de una vez
          initializeAllFormData(settings);
        } else {
          throw new Error('Error al cargar las configuraciones');
        }
      } catch (error) {
        console.error('SystemConfig: Error loading settings:', error);
        setError('Error al cargar las configuraciones del sistema. Por favor, intente nuevamente.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []); // Solo se ejecuta al montar el componente

  const initializeAllFormData = (settings) => {
    if (!settings) return;

    const newFormData = {
      regional: settings.REGIONAL ? {
        currency: settings.REGIONAL.currency.default,
        timeZone: settings.REGIONAL.timezone.default,
        dateFormat: settings.REGIONAL.dateFormat.default,
        timeFormat: settings.REGIONAL.timeFormat.default,
        decimalSeparator: settings.REGIONAL.decimalSeparator.default,
        thousandsSeparator: settings.REGIONAL.thousandsSeparator.default,
        decimalPlaces: settings.REGIONAL.decimalPlaces.default,
        alwaysShowDecimals: settings.REGIONAL.alwaysShowDecimals.default
      } : null,

      application: settings.APPLICATION ? {
        theme: settings.APPLICATION.theme?.default || 'system',
        language: settings.APPLICATION.language?.default || 'es',
        sessionTimeout: settings.APPLICATION.sessionTimeout?.default || '30',
        itemsPerPage: settings.APPLICATION.itemsPerPage?.default || '25',
        notifications: settings.APPLICATION.notifications?.default || true,
        autoSave: settings.APPLICATION.autoSave?.default || true
      } : null,

      company: settings.COMPANY ? {
        rut: settings.COMPANY.rut.default || '',
        name: settings.COMPANY.name.default || '',
        email: settings.COMPANY.email.default || '',
        phone: settings.COMPANY.phone.default || '',
        address: settings.COMPANY.address.default || '',
        website: settings.COMPANY.website.default || '',
        logo: settings.COMPANY.logo.default || null
      } : null,

      security: settings.SECURITY ? {
        auditLog: settings.SECURITY.auditLog.default || false,
        loginAttempts: settings.SECURITY.loginAttempts.default || '5',
        twoFactorAuth: settings.SECURITY.twoFactorAuth.default || false,
        passwordPolicy: settings.SECURITY.passwordPolicy.default || 'medium',
        sessionTimeout: settings.SECURITY.sessionTimeout.default || '30',
        emailNotifications: settings.SECURITY.emailNotifications.default || true
      } : null,

      notifications: settings.DELIVERY_NOTIFICATIONS ? {
        cargoType: settings.DELIVERY_NOTIFICATIONS.defaultValues.selectedCargoType || 'general',
        requiredAttachments: [...settings.DELIVERY_NOTIFICATIONS.defaultValues.requiredAttachments],
        statusLabels: { ...settings.DELIVERY_NOTIFICATIONS.defaultValues.statusLabels }
      } : null,

      orders: settings.PURCHASE_ORDERS ? {
        orderStatusLabels: settings.PURCHASE_ORDERS.statusOptions.reduce((acc, status) => ({
          ...acc,
          [status.value.toLowerCase()]: status.customLabel
        }), {}),
        schedulingType: settings.PURCHASE_ORDERS.schedulingType?.default || 'location'
      } : null,

      documents: settings.DOCUMENT_SEQUENCE ? {
        sequences: settings.DOCUMENT_SEQUENCE.documents.reduce((acc, doc) => ({
          ...acc,
          [doc.id]: {
            currentSequence: '1',
            from: '1',
            to: '999999',
            autoAssigned: settings.DOCUMENT_SEQUENCE.sequenceConfig.autoAssigned.default || false
          }
        }), {})
      } : null,

      integrations: settings.INTEGRATIONS ? {
        selectedSystem: settings.INTEGRATIONS.system?.default || 'none',
        sapb1: {
          baseUrl:   settings.INTEGRATIONS.sapb1?.baseUrl?.default   || '',
          companyDb: settings.INTEGRATIONS.sapb1?.companyDb?.default || '',
          userName:  settings.INTEGRATIONS.sapb1?.userName?.default  || '',
          password:  settings.INTEGRATIONS.sapb1?.password?.default  || ''
        }
      } : null,

      farosbi: settings.FAROSBI ? {
        reportId:        settings.FAROSBI.reports?.reportId        || '',
        tenantId:        settings.FAROSBI.reports?.tenantId        || '',
        token:           settings.FAROSBI.reports?.token           || '',
        companyId:       settings.FAROSBI.reports?.companyId       || '',
        multipleReports: settings.FAROSBI.reports?.multipleReports ?? false,
        baseUrl:         settings.FAROSBI.reports?.baseUrl         || '',
        filtro:          settings.FAROSBI.reports?.filtro          || '',
        criterio:        settings.FAROSBI.reports?.criterio        || '',
      } : {
        reportId: '', tenantId: '', token: '', companyId: '', multipleReports: false,
        baseUrl: '', filtro: '', criterio: ''
      }
    };

    console.log('Initialized form data:', newFormData); // Para debugging
    setAllFormData(newFormData);
  };

  const handleTabSelect = useCallback((event) => {
    const tabElement = event.detail.tab;
    const tabId = tabElement.getAttribute('data-tab-id');
    console.log('SystemConfig: Tab selection - Current:', activeTab, 'New:', tabId);
    setActiveTab(tabId);
  }, [activeTab]);

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    setIsLoading(true);
    
    try {
      const userDataString = localStorage.getItem("userData");
      let tenantId;
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        tenantId = userData.tenantId;
      }

      // Preparar los datos para guardar
      const updatedSettings = {
        ...systemSettings,
        REGIONAL: allFormData.regional ? {
          ...systemSettings.REGIONAL,
          currency: { 
            ...systemSettings.REGIONAL.currency,
            default: allFormData.regional.currency
          },
          timezone: { 
            ...systemSettings.REGIONAL.timezone,
            default: allFormData.regional.timeZone
          },
          dateFormat: { 
            ...systemSettings.REGIONAL.dateFormat,
            default: allFormData.regional.dateFormat
          },
          timeFormat: { 
            ...systemSettings.REGIONAL.timeFormat,
            default: allFormData.regional.timeFormat
          },
          decimalSeparator: { 
            ...systemSettings.REGIONAL.decimalSeparator,
            default: allFormData.regional.decimalSeparator
          },
          thousandsSeparator: { 
            ...systemSettings.REGIONAL.thousandsSeparator,
            default: allFormData.regional.thousandsSeparator
          },
          decimalPlaces: { 
            ...systemSettings.REGIONAL.decimalPlaces,
            default: allFormData.regional.decimalPlaces
          },
          alwaysShowDecimals: { 
            ...systemSettings.REGIONAL.alwaysShowDecimals,
            default: allFormData.regional.alwaysShowDecimals
          }
        } : systemSettings.REGIONAL,

        APPLICATION: allFormData.application ? {
          ...systemSettings.APPLICATION,
          theme: { ...systemSettings.APPLICATION.theme, default: allFormData.application.theme },
          language: { ...systemSettings.APPLICATION.language, default: allFormData.application.language },
          sessionTimeout: { ...systemSettings.APPLICATION.sessionTimeout, default: allFormData.application.sessionTimeout },
          itemsPerPage: { ...systemSettings.APPLICATION.itemsPerPage, default: allFormData.application.itemsPerPage },
          notifications: { ...systemSettings.APPLICATION.notifications, default: allFormData.application.notifications },
          autoSave: { ...systemSettings.APPLICATION.autoSave, default: allFormData.application.autoSave }
        } : systemSettings.APPLICATION,

        COMPANY: allFormData.company ? {
          ...systemSettings.COMPANY,
          rut: { ...systemSettings.COMPANY.rut, default: allFormData.company.rut },
          name: { ...systemSettings.COMPANY.name, default: allFormData.company.name },
          email: { ...systemSettings.COMPANY.email, default: allFormData.company.email },
          phone: { ...systemSettings.COMPANY.phone, default: allFormData.company.phone },
          address: { ...systemSettings.COMPANY.address, default: allFormData.company.address },
          website: { ...systemSettings.COMPANY.website, default: allFormData.company.website },
          logo: { ...systemSettings.COMPANY.logo, default: allFormData.company.logo }
        } : systemSettings.COMPANY,

        SECURITY: allFormData.security ? {
          ...systemSettings.SECURITY,
          auditLog: { ...systemSettings.SECURITY.auditLog, default: allFormData.security.auditLog },
          loginAttempts: { ...systemSettings.SECURITY.loginAttempts, default: allFormData.security.loginAttempts },
          twoFactorAuth: { ...systemSettings.SECURITY.twoFactorAuth, default: allFormData.security.twoFactorAuth },
          passwordPolicy: { ...systemSettings.SECURITY.passwordPolicy, default: allFormData.security.passwordPolicy },
          sessionTimeout: { ...systemSettings.SECURITY.sessionTimeout, default: allFormData.security.sessionTimeout },
          emailNotifications: { ...systemSettings.SECURITY.emailNotifications, default: allFormData.security.emailNotifications }
        } : systemSettings.SECURITY,

        DELIVERY_NOTIFICATIONS: allFormData.notifications ? {
          ...systemSettings.DELIVERY_NOTIFICATIONS,
          defaultValues: {
            ...systemSettings.DELIVERY_NOTIFICATIONS.defaultValues,
            selectedCargoType: allFormData.notifications.cargoType,
            requiredAttachments: allFormData.notifications.requiredAttachments,
            statusLabels: allFormData.notifications.statusLabels
          }
        } : systemSettings.DELIVERY_NOTIFICATIONS,

        PURCHASE_ORDERS: allFormData.orders ? {
          ...systemSettings.PURCHASE_ORDERS,
          statusOptions: systemSettings.PURCHASE_ORDERS.statusOptions.map(status => ({
            ...status,
            customLabel: allFormData.orders.orderStatusLabels[status.value.toLowerCase()]
          })),
          schedulingType: {
            ...systemSettings.PURCHASE_ORDERS.schedulingType,
            default: allFormData.orders.schedulingType
          }
        } : systemSettings.PURCHASE_ORDERS,

        DOCUMENT_SEQUENCE: allFormData.documents ? {
          ...systemSettings.DOCUMENT_SEQUENCE,
          documents: systemSettings.DOCUMENT_SEQUENCE.documents,
          sequenceConfig: {
            ...systemSettings.DOCUMENT_SEQUENCE.sequenceConfig,
            autoAssigned: {
              ...systemSettings.DOCUMENT_SEQUENCE.sequenceConfig.autoAssigned,
              default: Object.values(allFormData.documents.sequences).some(seq => seq.autoAssigned)
            }
          }
        } : systemSettings.DOCUMENT_SEQUENCE,

        INTEGRATIONS: allFormData.integrations && systemSettings.INTEGRATIONS ? {
          ...systemSettings.INTEGRATIONS,
          system: {
            ...systemSettings.INTEGRATIONS.system,
            default: allFormData.integrations.selectedSystem
          },
          sapb1: {
            ...systemSettings.INTEGRATIONS.sapb1,
            enabled: allFormData.integrations.selectedSystem === 'sapb1',
            baseUrl:   { ...systemSettings.INTEGRATIONS.sapb1.baseUrl,   default: allFormData.integrations.sapb1.baseUrl },
            companyDb: { ...systemSettings.INTEGRATIONS.sapb1.companyDb, default: allFormData.integrations.sapb1.companyDb },
            userName:  { ...systemSettings.INTEGRATIONS.sapb1.userName,  default: allFormData.integrations.sapb1.userName },
            password:  { ...systemSettings.INTEGRATIONS.sapb1.password,  default: allFormData.integrations.sapb1.password }
          }
        } : systemSettings.INTEGRATIONS
      };

      // Hacer la llamada PUT para cada sección que tenga cambios
      const updatePromises = [];

      if (allFormData.regional) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/REGIONAL`,
            updatedSettings.REGIONAL
          )
        );
      }

      if (allFormData.application) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/APPLICATION`,
            updatedSettings.APPLICATION
          )
        );
      }

      if (allFormData.company) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/COMPANY`,
            updatedSettings.COMPANY
          )
        );
      }

      if (allFormData.security) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/SECURITY`,
            updatedSettings.SECURITY
          )
        );
      }

      if (allFormData.notifications) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/DELIVERY_NOTIFICATIONS`,
            updatedSettings.DELIVERY_NOTIFICATIONS
          )
        );
      }

      if (allFormData.orders) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/PURCHASE_ORDERS`,
            updatedSettings.PURCHASE_ORDERS
          )
        );
      }

      if (allFormData.documents) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/DOCUMENT_SEQUENCE`,
            updatedSettings.DOCUMENT_SEQUENCE
          )
        );
      }

      if (allFormData.integrations) {
        updatePromises.push(
          axiosInstance.put(
            `tenant-configurations/${tenantId}/INTEGRATIONS`,
            updatedSettings.INTEGRATIONS
          )
        );
      }

      // Esperar a que todas las actualizaciones se completen
      const results = await Promise.all(updatePromises);

      // Verificar si todas las actualizaciones fueron exitosas
      const allSuccessful = results.every(response => response.data.code === 'SUCCESS');

      if (allSuccessful) {
        setSystemSettings(updatedSettings);
      } else {
        throw new Error('Error al guardar algunas configuraciones');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Error al guardar las configuraciones. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabSave = async (tabId, updatedSettings) => {
    setIsLoading(true);
    try {
      const userDataString = localStorage.getItem("userData");
      let tenantId;
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        tenantId = userData.tenantId;
      } else {
        throw new Error("No se encontró userData en localStorage.");
      }

      const response = await axiosInstance.put(
        `tenant-configurations/${tenantId}/${tabId}`,
        updatedSettings
      );

      if (response.data.code === 'SUCCESS') {
        // Actualizar solo la sección correspondiente en systemSettings
        setSystemSettings(prev => ({
          ...prev,
          [tabId]: updatedSettings
        }));
      } else {
        throw new Error('Error al guardar las configuraciones');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError('Error al guardar las configuraciones. Por favor, intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormDataChange = (tabId, newData) => {
    setAllFormData(prev => ({
      ...prev,
      [tabId]: newData
    }));
  };

  const renderTabContent = useCallback(() => {
    if (error) {
      return (
        <div style={{ color: 'red', padding: '1rem' }}>
          {error}
        </div>
      );
    }

    const commonProps = {
      systemSettings: systemSettings,
      setFormData: (newData) => handleFormDataChange(activeTab, newData),
      isLoading: isLoading
    };

    switch(activeTab) {
      case "regional":
        return (
          <RegionalConfig 
            {...commonProps}
            formData={allFormData.regional}
            onSave={(updatedSettings) => handleTabSave('REGIONAL', updatedSettings)}
          />
        );
      case "application":
        return (
          <ApplicationConfig
            {...commonProps}
            formData={allFormData.application}
            onSave={(updatedSettings) => handleTabSave('APPLICATION', updatedSettings)}
          />
        );
      case "company":
        return (
          <CompanyConfig
            {...commonProps}
            formData={allFormData.company}
            systemSettings={systemSettings?.COMPANY}
            onSave={(updatedSettings) => handleTabSave('COMPANY', updatedSettings)}
          />
        );
      case "security":
        return (
          <SecurityConfig
            {...commonProps}
            formData={allFormData.security}
            systemSettings={systemSettings}  // Pasamos todo systemSettings porque contiene SECURITY
            onSave={(updatedSettings) => handleTabSave('SECURITY', updatedSettings)}
          />
        );
      case "notifications":
        return (
          <NotificationsConfig
            {...commonProps}
            formData={allFormData.notifications}
            systemSettings={systemSettings}  // Pasamos todo systemSettings porque contiene DELIVERY_NOTIFICATIONS
            onSave={(updatedSettings) => handleTabSave('DELIVERY_NOTIFICATIONS', updatedSettings)}
          />
        );
      case "orders":
        return (
          <OrdersConfig
            {...commonProps}
            formData={allFormData.orders}
            systemSettings={systemSettings}  // Pasamos todo systemSettings porque contiene PURCHASE_ORDERS
            onSave={(updatedSettings) => handleTabSave('PURCHASE_ORDERS', updatedSettings)}
          />
        );
      case "documents":
        return (
          <DocumentsConfig
            {...commonProps}
            formData={allFormData.documents}
            systemSettings={systemSettings}  // Pasamos todo systemSettings porque contiene DOCUMENT_SEQUENCE
            onSave={(updatedSettings) => handleTabSave('DOCUMENT_SEQUENCE', updatedSettings)}
          />
        );
      case "integrations":
        return (
          <IntegrationsConfig
            {...commonProps}
            formData={allFormData.integrations}
            systemSettings={systemSettings}
            onSave={(updatedSettings) => handleTabSave('INTEGRATIONS', updatedSettings)}
          />
        );
      case "farosbi":
        return (
          <FarosBiConfig
            {...commonProps}
            formData={allFormData.farosbi}
            systemSettings={systemSettings}
            onSave={(updatedSettings) => handleTabSave('FAROSBI', updatedSettings)}
          />
        );
      default:
        console.warn('SystemConfig: Unknown tab:', activeTab);
        return null;
    }
  }, [activeTab, allFormData, systemSettings, isLoading, error]);

  return (
    <Layout>
      <div style={{ backgroundColor: 'var(--sapBackgroundColor)', borderRadius: '0.5rem', padding: '1rem' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem',
          marginBottom: '1.5rem',
          borderBottom: '1px solid var(--sapGroup_TitleBorderColor)',
          paddingBottom: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Icon name="action-settings" style={{ fontSize: '1.75rem', color: 'var(--sapHighlightColor)' }} />
            <div>
              <Title level="H2" style={{ margin: 0 }}>{autoT('Configuraciones del Sistema')}</Title>
              <Text style={{ color: 'var(--sapContent_LabelColor)', fontSize: 'var(--sapFontSmallSize)', display: 'block', marginTop: '0.25rem' }}>
                {autoT('Personaliza y configura el comportamiento de la aplicación según tus necesidades')}
              </Text>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <TabContainer
            onTabSelect={handleTabSelect}
            fixed={true}
            showOverflow={true}
          >
            <Tab
              text={autoT('Regional')}
              icon="world"
              data-tab-id="regional"
              selected={activeTab === "regional"}
            />
            <Tab
              text={autoT('Aplicación')}
              icon="action-settings"
              data-tab-id="application"
              selected={activeTab === "application"}
            />
            <Tab
              text={autoT('Empresa')}
              icon="company-view"
              data-tab-id="company"
              selected={activeTab === "company"}
            />
            <Tab
              text={autoT('Notificaciones')}
              icon="bell"
              data-tab-id="notifications"
              selected={activeTab === "notifications"}
            />
            <Tab
              text={autoT('Órdenes')}
              icon="sales-order"
              data-tab-id="orders"
              selected={activeTab === "orders"}
            />
            <Tab
              text={autoT('Documentos')}
              icon="documents"
              data-tab-id="documents"
              selected={activeTab === "documents"}
            />
            <Tab
              text={autoT('Seguridad')}
              icon="shield"
              data-tab-id="security"
              selected={activeTab === "security"}
            />
            <Tab
              text={autoT('Integraciones')}
              icon="connected"
              data-tab-id="integrations"
              selected={activeTab === "integrations"}
            />
            <Tab
              text="FAROSBI"
              icon="business-objects-experience"
              data-tab-id="farosbi"
              selected={activeTab === "farosbi"}
            />
          </TabContainer>
        </div>

        {/* Content */}
        <div style={{ padding: '0.5rem 0' }}>
          {renderTabContent()}
        </div>

        {/* Dialog */}
        {showConfirmDialog && (
          <MessageBox
            type={MessageBoxType.CONFIRM}
            onClose={() => setShowConfirmDialog(false)}
            onConfirm={handleConfirmSave}
            title={autoT('Confirmar Cambios')}
          >
            {autoT('¿Estás seguro de que deseas guardar los cambios? Esta acción puede afectar el comportamiento del sistema.')}
          </MessageBox>
        )}
      </div>
    </Layout>
  );
}

export default SystemConfig; 